import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { useAssistant } from '../../contexts/AssistantContext';
import { useAuth } from '../../contexts/AuthContext';
import { colors, spacing, typography } from '../../core/theme/tokens';
import {
  listPinnedWidgets,
  unpinWidget,
  type PinnedWidget,
} from '../../services/pinnedWidgetService';
import {
  notifyPinnedWidgetsChanged,
  subscribePinnedWidgetsChanged,
} from '../../services/pinnedWidgetsBus';
import { subscribeDashboardRefresh } from '../../services/dashboardRefreshBus';
import { ErrorRetry } from '../ui/ErrorRetry';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';

/**
 * Pins from AI chat — shown on home in both Mine and Household.
 * Pins stay personal to the signed-in user; they are not household-scoped data.
 */
export function PinnedWidgetsStrip() {
  const { isAuthenticated } = useAuth();
  const { openAssistant, resumeThread } = useAssistant();
  const [pins, setPins] = useState<PinnedWidget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unpinningId, setUnpinningId] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const openingRef = useRef(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setPins([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await listPinnedWidgets();
      if (requestId !== requestIdRef.current) return;
      setPins(res.pinnedWidgets ?? []);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setPins([]);
      setError(err instanceof Error ? err.message : 'Could not load pins');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return subscribePinnedWidgetsChanged(() => {
      void load();
    });
  }, [load]);

  useEffect(() => {
    return subscribeDashboardRefresh(() => {
      void load();
    });
  }, [load]);

  const onOpenPin = useCallback(
    async (pin: PinnedWidget) => {
      if (openingRef.current) return;
      openingRef.current = true;
      setOpeningId(pin.id);
      try {
        if (pin.sourceThreadId) {
          await resumeThread(pin.sourceThreadId);
        } else {
          await openAssistant();
        }
      } finally {
        openingRef.current = false;
        setOpeningId(null);
      }
    },
    [openAssistant, resumeThread],
  );

  const onUnpin = useCallback(
    async (id: string) => {
      setUnpinningId(id);
      try {
        await unpinWidget(id);
        setPins((prev) => prev.filter((p) => p.id !== id));
        notifyPinnedWidgetsChanged();
      } catch {
        // keep card; user can retry
      } finally {
        setUnpinningId(null);
      }
    },
    [],
  );

  if (!isAuthenticated) return null;
  if (!loading && pins.length === 0 && !error) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.headingRow}>
        <Text style={styles.heading}>Your pins from chat</Text>
        <RefreshButton onPress={() => void load()} refreshing={loading} compact />
      </View>
      {error && pins.length === 0 ? (
        <ErrorRetry message={error} onRetry={() => void load()} refreshing={loading} compact />
      ) : null}
      {loading && pins.length === 0 ? (
        <ActivityIndicator color={colors.accent} />
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.row}
        >
          {pins.map((pin) => (
            <View key={pin.id} style={styles.cardPress}>
              <Pressable
                onPress={() => void onOpenPin(pin)}
                disabled={Boolean(openingId)}
                style={({ pressed }) => [pressed && styles.pressed, styles.webPointer]}
                accessibilityRole="button"
                accessibilityLabel={`Open chat: ${pin.title}`}
              >
                <GlassCard style={styles.card}>
                  <Text style={styles.badge}>Pinned</Text>
                  <Text style={styles.title} numberOfLines={2}>
                    {pin.title}
                  </Text>
                  <Text style={styles.body} numberOfLines={4}>
                    {pin.body}
                  </Text>
                  <Text style={styles.drillHint}>
                    {openingId === pin.id ? 'Opening…' : 'Open chat →'}
                  </Text>
                </GlassCard>
              </Pressable>
              <Pressable
                onPress={() => void onUnpin(pin.id)}
                disabled={unpinningId === pin.id}
                style={({ pressed }) => [styles.unpinBtn, pressed && styles.pressed]}
                accessibilityRole="button"
                accessibilityLabel={`Unpin ${pin.title}`}
              >
                <Text style={styles.unpinCta}>
                  {unpinningId === pin.id ? 'Unpinning…' : 'Unpin'}
                </Text>
              </Pressable>
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { marginBottom: spacing.lg, gap: spacing.sm },
  headingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  heading: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  row: { gap: spacing.md, paddingRight: spacing.md },
  cardPress: { width: 260 },
  card: { gap: spacing.xs, minHeight: 120 },
  badge: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '800',
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  title: { ...typography.headline, fontSize: 16, color: colors.text },
  body: { ...typography.body, fontSize: 13, color: colors.textMuted, lineHeight: 18 },
  drillHint: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.accent,
    marginTop: spacing.xs,
  },
  webPointer: { cursor: 'pointer' },
  unpinBtn: {
    marginTop: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    alignSelf: 'flex-start',
  },
  unpinCta: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
  },
  pressed: { opacity: 0.9, transform: [{ scale: 0.99 }] },
});
